import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Button } from "components/ui/button";
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { useAddGroupMembers, useGetUsersQuery } from "../../hooks/use-iam";
import { useCommonStore } from "state/store/common/useCommonStore";
import { Search, X, Users } from 'lucide-react';
import { debounce } from 'lodash';
import { useTheme } from "components/core/theme-provider";
import { useTranslation } from "react-i18next";

/**
 * AddMembersToGroupModal Component
 *
 * A dialog component for adding members to an existing group. Features user search,
 * multi-select capability, and displays selected users as removable chips.
 *
 * Features:
 * - Debounced user search functionality
 * - Multi-select users with visual feedback
 * - Selected users displayed as removable chips
 * - Submits via `useGroupCreate` mutation to update group membership
 * - Styled using Tailwind and ShadCN dialog components
 *
 * Props:
 * - `onClose` (function): Callback to close the dialog
 * - `selectedGroup` (object): The group to add members to
 * - `reloadData` (function, optional): Callback to reload data after adding members
 * - `isOpen` (boolean, optional): For compatibility with existing usage
 *
 * @param {AddMembersToGroupModalProps} props - Component props
 * @returns {JSX.Element} A modal with member selection interface
 *
 * @example
 * <AddMembersToGroupModal 
 *   onClose={() => setIsDialogOpen(false)} 
 *   selectedGroup={group}
 *   reloadData={fetchGroups}
 * />
 */

interface AddMembersToGroupModalProps {
    isOpen?: boolean;
    onClose: () => void;
    selectedGroup?: any;
    reloadData?: () => void;
}

export const AddMembersToGroupModal: React.FC<AddMembersToGroupModalProps> = ({
    isOpen,
    onClose,
    selectedGroup,
    reloadData,
}) => {
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [inputValue, setInputValue] = useState('');

    const { mutateAsync: addMembersToGroup } = useAddGroupMembers();
    const user = useCommonStore((state) => state.user);
    const theme = useTheme();
    const { t } = useTranslation();

    const debouncedChange = useMemo(
        () =>
            debounce((val: string) => {
                setSearchQuery(val);
            }, 500),
        []
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedChange(value);
    };

    const queryFilter = useMemo(() => {
        if (searchQuery) {
            return {
                name: searchQuery,
                excludeGroupId: selectedGroup?.id || '',
                IsOrgSpecific: true,
            };
        }
        return null;
    }, [searchQuery]);

    const {
        data,
        isLoading: isUsersLoading,
    } = useGetUsersQuery({
        page: 0,
        pageSize: 50,
        filter: queryFilter || {},
        enabled: !!(isOpen && queryFilter),
    });

    const allUsers = data?.data || [];

    const handleUserToggle = useCallback((user: any) => {
        setSelectedUsers(prev => {
            const isSelected = prev.some(u => u.itemId === user.itemId);
            if (isSelected) {
                return prev.filter(u => u.itemId !== user.itemId);
            } else {
                return [...prev, user];
            }
        });
    }, []);

    const handleRemoveUser = useCallback((userId: string) => {
        setSelectedUsers(prev => prev.filter(u => u.itemId !== userId));
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSelectedUsers([]);
            setSearchQuery('');
            setInputValue('');
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        if (!selectedGroup?.id) return;
        e.preventDefault();
        const payload = {
            "ItemId": selectedGroup.id || '',
            "GroupName": selectedGroup.groupName || '',
            "GroupType": "Project",
            "UserIds": selectedUsers.map(user => user.itemId),
            "OwnerUserId": user?.id || "",
            "Source": "internal",
        }
        onClose();
        try {
            const response = await addMembersToGroup(payload);
            reloadData?.();
            return response;
        } catch (error) {
            console.error(error);
        }
    };

    const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <DialogContent
            className="rounded-md sm:max-w-[500px] overflow-y-auto max-h-screen"
            onClick={handleDialogClick}
        >
            <DialogHeader>
                <DialogTitle className="mb-2">{t('ADD_MEMBERS')}</DialogTitle>
                <DialogDescription className="text-medium-emphasis font-normal">
                    {t('ADD_MEMBERS_TO_GROUP_DESCRIPTION')}
                </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div>
                    <Label className="mb-4 block">{t('SEARCH_MEMBER')}</Label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary" />
                        <Input
                            type="text"
                            placeholder={t('USERS_NAME')}
                            value={inputValue}
                            onChange={handleInputChange}
                            className="
                                        h-10 w-full rounded-3xl
                                        border border-gray-300
                                        bg-background pl-9 pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    "
                        />
                    </div>
                </div>

                {selectedUsers.length > 0 && (
                    <div>
                        <div className="flex max-h-[80px] flex-wrap gap-2 overflow-y-auto p-3 rounded-md">
                            {selectedUsers.map(user => (
                                <div
                                    key={user.itemId}
                                    className="flex items-center gap-1 bg-primary  text-gray-50 px-2 py-1 rounded text-xs"
                                >
                                    <span>{user.firstName} {user.lastName}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveUser(user.itemId)}
                                        className="hover:text-blue-200"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <div className="border rounded-md max-h-60 overflow-y-auto">
                        {searchQuery ? (
                            <div className="space-y-1 p-2">
                                {allUsers.map((userItem) => {
                                    const isSelected = selectedUsers.some(u => u.itemId === userItem.itemId);
                                    return (
                                        <button
                                            key={userItem.itemId}
                                            type="button"
                                            onClick={() => handleUserToggle(userItem)}
                                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${theme.theme === 'dark' ? 'hover:bg-[#272727]' : 'hover:bg-gray-100'
                                                } w-full text-left ${isSelected ? 'bg-primary/10 border border-primary' : ''
                                                }`}
                                        >
                                            <div className="flex-1">
                                                <div className={`text-sm font-medium ${theme.theme === 'dark' ? 'text-primary' : 'text-gray-900'
                                                    }`}>
                                                    {userItem.firstName} {userItem.lastName}
                                                </div>
                                                <div className="text-xs text-gray-500">{userItem.email}</div>
                                            </div>
                                            {isSelected && <Users className="w-4 h-4 text-primary" />}
                                        </button>
                                    );
                                })}

                                {isUsersLoading && (
                                    <div className="text-center py-4 text-sm text-gray-500">
                                        {t('LOADING_USERS')}...
                                    </div>
                                )}

                                {allUsers.length === 0 && !isUsersLoading && (
                                    <div className="text-center py-4 text-sm text-gray-500">
                                        {t('NO_USERS_FOUND')}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center text-gray-500 text-sm py-12">
                                {t('ENTER_A_NAME_TO_SEARCH_USERS')}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="mt-5 flex justify-end gap-2">
                    <Button variant="outline" type="button" onClick={onClose}>
                        {t('CANCEL')}
                    </Button>
                    <Button
                        type="submit"
                        disabled={selectedUsers.length === 0}
                    >
                        {t('ADD_MEMBERS')} ({selectedUsers.length})
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
};